import { z } from "zod";
import { CENTS_PER_EURO } from "../configs/unpaidInvoices.config";
import type { UnpaidInvoicesStatus } from "../types/unpaidInvoices.types";

const DECIMAL_EUROS_PATTERN = /^\d+(\.\d{1,2})?$/;

const eurosAmountSchema = z.union([z.number().min(0), z.string().regex(DECIMAL_EUROS_PATTERN)]);

// Card payments are excluded on purpose: they settle instantly, so they never leave a payment "pending".
const pendingPaymentSchema = z.object({
  method: z.enum(["bank_transfer", "sepa_debit"]),
  amount: eurosAmountSchema,
});

const unpaidInvoicesSchema = z.object({
  unpaid_invoices: z
    .object({
      amount: eurosAmountSchema.nullable(),
      is_blocked: z.boolean().default(false),
      pending_payment: pendingPaymentSchema.nullish(),
      // Asked by a person in the CRM, with the message they wrote: older APIs send neither.
      payment_method_required: z.boolean().default(false),
      message: z.string().nullish(),
    })
    .nullish(),
});

// Rounding absorbs float drift (1.15 * 100 = 114.99999999999999).
const eurosToCents = (euros: number | string): number => Math.round(Number(euros) * CENTS_PER_EURO);

// An invalid payload must never block elected members: any doubt resolves to "no unpaid invoices".
export const parseUnpaidInvoices = (organization: unknown): UnpaidInvoicesStatus | null => {
  const parsed = unpaidInvoicesSchema.safeParse(organization);
  if (!parsed.success || !parsed.data.unpaid_invoices) return null;

  const {
    amount, is_blocked, pending_payment, payment_method_required, message,
  } = parsed.data.unpaid_invoices;
  return {
    amountCents: amount === null ? null : eurosToCents(amount),
    isBlocked: is_blocked,
    pendingPayment: pending_payment
      ? { method: pending_payment.method, amountCents: eurosToCents(pending_payment.amount) }
      : null,
    // Only when asked: a status without them reads as before.
    ...(payment_method_required && { paymentMethodRequired: true }),
    ...(message?.trim() && { staffMessage: message.trim() }),
  };
};
